import React, { useState, useEffect } from 'react'
import { FiMoreVertical, FiEye, FiEyeOff, FiUpload } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'


import { useApp } from '../contexts/AppContext'
import { usersService } from '../services/usersService'
import { reviewsService } from '../services/reviewsService'
import userAvatarOther from '../assets/images/user-avatar-other.jpg'
import userAvatar from '../assets/images/user-avatar.png'
import { exportToCSV } from '../utils/csvExport'

const UsersPage = () => {
  const { users, loading, error, dispatch, actions } = useApp()
  const [actionMenuOpen, setActionMenuOpen] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedAccount, setSelectedAccount] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [expandedUserDetails, setExpandedUserDetails] = useState(null)
  const [userReviews, setUserReviews] = useState({})
  const [loadingReviews, setLoadingReviews] = useState({})
  const navigate = useNavigate()

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers()
  }, [])

  // Auto-refresh every 1 minute
  useEffect(() => {
    const interval = setInterval(() => {
      fetchUsers();
    }, 60000);

    return () => clearInterval(interval); // cleanup
  }, []);


  const handleRowClick = (userName, userRole) => {
    // URL encode the username to handle spaces and special characters
    const encodedUserName = encodeURIComponent(userName)
    navigate(`/orders/${encodedUserName}/${userRole}`)
  }

  const fetchUsers = async () => {
    dispatch({ type: actions.SET_USERS_LOADING, payload: true })

    try {
      const result = await usersService.getAllUsers()

      if (result.success) {
        dispatch({ type: actions.SET_USERS_SUCCESS, payload: result.data })
      } else {
        dispatch({ type: actions.SET_USERS_ERROR, payload: result.error })
        toast.error(result.message || 'Failed to fetch users')
      }
    } catch (error) {
      dispatch({ type: actions.SET_USERS_ERROR, payload: error.message })
      toast.error('An error occurred while fetching users')
    }
  }

  const [formData, setFormData] = useState({
    role: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    image: null,
  })
  const [showPassword, setShowPassword] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)

  const toggleActionMenu = (index) => {
    setActionMenuOpen(actionMenuOpen === index ? null : index)
  }

  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData({ ...formData, image: file })
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleRoleChange = (role) => {
    setFormData({ ...formData, role })
  }

  const handleAddUser = async () => {
    if (!formData.name || !formData.email || !formData.password || !formData.role) {
      toast.error('Please fill in all required fields')
      return
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role.toLowerCase(),
        phone: '1234567890', // Default phone for now
      }

      const result = await usersService.createUser(userData)

      if (result.success) {
        dispatch({ type: actions.ADD_USER, payload: result.data })
        toast.success(result.message || 'User created successfully!')
        setShowModal(false)
        setFormData({
          role: '',
          name: '',
          email: '',
          password: '',
          confirmPassword: '',
          image: null,
        })
        setImagePreview(null)
      } else {
        toast.error(result.error || 'Failed to create user')
      }
    } catch (error) {
      toast.error('An error occurred while creating user')
    }
  }

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const result = await usersService.deleteUser(userId)

        if (result.success) {
          dispatch({ type: actions.DELETE_USER, payload: userId })
          toast.success('User deleted successfully')
        } else {
          toast.error(result.message || 'Failed to delete user')
        }
      } catch (error) {
        toast.error('An error occurred while deleting user')
      }
    }
  }

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      const result = await usersService.updateUserRole(userId, newRole)

      if (result.success) {
        dispatch({ type: actions.UPDATE_USER, payload: result.data })
        toast.success(`User role updated to ${newRole}`)
      } else {
        toast.error(result.message || 'Failed to update user role')
      }
    } catch (error) {
      toast.error('An error occurred while updating user role')
    }
  }

  const handleBlockUser = async (userId, isApproved) => {
    try {
      const result = await usersService.updateUserApproval(userId, !isApproved)

      if (result.success) {
        dispatch({ type: actions.UPDATE_USER, payload: result.data })
        toast.success(`User ${!isApproved ? 'approved' : 'blocked'} successfully`)
      } else {
        toast.error(result.message || `Failed to ${!isApproved ? 'approve' : 'block'} user`)
      }
    } catch (error) {
      toast.error(`An error occurred while ${!isApproved ? 'approving' : 'blocking'} user`)
    }
  }

  const handleApproveTrucker = async (userId) => {
    try {
      const result = await usersService.approveUser(userId)

      if (result.success) {
        dispatch({ type: actions.UPDATE_USER, payload: result.data })
        toast.success('Trucker approved successfully')
      } else {
        toast.error(result.message || 'Failed to approve trucker')
      }
    } catch (error) {
      toast.error('An error occurred while approving trucker')
    }
  }

  const fetchUserReviews = async (userId) => {
    if (userReviews[userId]) return

    setLoadingReviews(prev => ({ ...prev, [userId]: true }))
    try {
      const result = await reviewsService.getUserReviews(userId)
      if (result.success) {
        setUserReviews(prev => ({ ...prev, [userId]: result.data }))
      }
    } catch (error) {
      // Silently fail - reviews are optional
    } finally {
      setLoadingReviews(prev => ({ ...prev, [userId]: false }))
    }
  }

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      user.phone?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      user.company?.toLowerCase()?.includes(searchTerm.toLowerCase())
    const matchesAccount =
      selectedAccount === 'All Users' ||
      selectedAccount === '' ||
      (selectedAccount === 'Admins' && user.role === 'admin') ||
      (selectedAccount === 'Customers' && user.role === 'customer') ||
      (selectedAccount === 'Truckers' && user.role === 'trucker') ||
      (selectedAccount === 'Drivers' && user.role === 'driver')

    return matchesSearch && matchesAccount
  }).sort((a, b) => {
    // Sort by createdAt date - most recent first
    const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
    const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
    return dateB - dateA // Descending order (newest first)
  })

  const handleExportCSV = () => {
    const headers = [
      { label: 'Full Name', key: 'name' },
      { label: 'Email Address', key: 'email' },
      { label: 'Phone Number', key: 'phone' },
      { label: 'Company', key: 'company' },
      { label: 'Role', key: 'role' },
      { label: 'Status', key: 'isApproved' },
      { label: 'Joined Date', key: 'createdAt' },
    ]

    // Transform data for CSV export
    const csvData = filteredUsers.map(user => ({
      ...user,
      isApproved: user.isApproved ? 'Approved' : 'Pending',
      role: user.role === 'trucker' ? 'BROKER' : user.role?.toUpperCase(),
      createdAt: user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
      phone: user.phone || 'N/A',
      company: user.company || 'N/A',
    }))

    const filename = `users_${new Date().toISOString().split('T')[0]}.csv`
    exportToCSV(csvData, headers, filename)
  }
  const roleBadge = (role) => {
    const map = {
      admin:    'bg-[#f0e6ff] text-[#8e44ad]',
      customer: 'bg-[#eaf4fb] text-[#3498db]',
      trucker:  'bg-[#eafaf1] text-[#27ae60]',
      driver:   'bg-[#fef5e7] text-[#e67e22]',
    }
    return map[role] || 'bg-[#f8f9fa] text-[#7f8c8d]'
  }

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
      {/* Page Header */}
      <div className="flex items-center justify-between pb-5 mb-6 border-b border-[#e9ecef]">
        <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Users</h1>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search users…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db] w-[220px]"
          />
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]"
          >
            <option value="All Users">All Users</option>
            <option value="Admins">Admins</option>
            <option value="Customers">Customers</option>
            <option value="Truckers">Brokers</option>
            <option value="Drivers">Drivers</option>
          </select>
          <button
            onClick={handleExportCSV}
            className="bg-[#3498db] hover:bg-[#2980b9] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Error state */}
      {error.users && (
        <div className="bg-[#fdedec] border border-[#e74c3c]/30 text-[#e74c3c] px-4 py-3 rounded-lg mb-5 text-sm">
          {error.users}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
              {['Full Name', 'Email', 'Phone', 'Company', 'Role', 'Status', 'Joined', 'Fleet', 'Actions'].map(h => (
                <th key={h} className="text-left px-5 py-3 text-xs font-medium text-[#7f8c8d] uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, idx) => (
              <React.Fragment key={user.id || idx}>
                <tr className="border-b border-[#e9ecef] hover:bg-[#f8f9fa] transition-colors">
                  <td className="px-5 py-3 font-medium text-[#2c3e50]">{user.name}</td>
                  <td className="px-5 py-3 text-[#7f8c8d]">{user.email || '—'}</td>
                  <td className="px-5 py-3 text-[#7f8c8d]">{user.phone || '—'}</td>
                  <td className="px-5 py-3 text-[#7f8c8d]">{user.company || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${roleBadge(user.role)}`}>
                      {user.role === 'trucker' ? 'BROKER' : user.role?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${user.isApproved ? 'bg-[#27ae60]' : 'bg-[#e74c3c]'}`} />
                      <span className="text-xs text-[#2c3e50]">{user.isApproved ? 'Approved' : 'Pending'}</span>
                      {user.isEmailVerified && <span className="text-xs text-[#27ae60]">✓</span>}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-[#7f8c8d]">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3">
                    {user.Vehicles?.length > 0 ? (
                      <span className="px-2 py-1 bg-[#eaf4fb] text-[#3498db] rounded text-xs font-semibold">
                        {user.Vehicles.length}v
                      </span>
                    ) : <span className="text-[#bdc3c7]">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => user.name && handleRowClick(user.name, user.role)}
                        className="px-3 py-1.5 bg-[#eafaf1] text-[#27ae60] hover:bg-[#27ae60] hover:text-white rounded text-xs font-medium transition-colors"
                      >
                        Orders
                      </button>
                      {(user.cnic || user.license || user.Vehicles?.length > 0) && (
                        <button
                          onClick={() => setExpandedUserDetails(expandedUserDetails === user.id ? null : user.id)}
                          className="px-3 py-1.5 bg-[#f0e6ff] text-[#8e44ad] hover:bg-[#8e44ad] hover:text-white rounded text-xs font-medium transition-colors"
                        >
                          {expandedUserDetails === user.id ? 'Hide' : 'Details'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expandable Details Row */}
                {expandedUserDetails === user.id && (
                  <tr className="border-b border-[#e9ecef]">
                    <td colSpan="9" className="px-5 py-5 bg-[#f8f9fa]">
                      <div className="space-y-4">
                        {/* Approve Button for Pending Truckers */}
                        {user.role === 'trucker' && !user.isApproved && (
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleApproveTrucker(user.id)}
                              className="px-4 py-2 bg-[#27ae60] text-white rounded-lg text-sm font-semibold hover:bg-[#219a52] transition-colors"
                            >
                              ✓ Approve Broker
                            </button>
                            <button
                              onClick={() => handleBlockUser(user.id, user.isApproved)}
                              className="px-4 py-2 bg-[#e74c3c] text-white rounded-lg text-sm font-semibold hover:bg-[#c0392b] transition-colors"
                            >
                              ✗ Reject
                            </button>
                          </div>
                        )}

                        {/* Documents */}
                        {(user.cnic || user.license || user.vehicleRegistration) && (
                          <div>
                            <p className="text-xs font-semibold text-[#7f8c8d] uppercase tracking-wide mb-2">Documents</p>
                            <div className="grid grid-cols-3 gap-3">
                              {user.cnic && (
                                <div className="p-3 bg-white border border-[#e9ecef] rounded-lg">
                                  <p className="text-xs text-[#7f8c8d] mb-1">CNIC</p>
                                  <p className="text-sm font-mono font-semibold text-[#2c3e50]">{user.cnic}</p>
                                </div>
                              )}
                              {user.license && (
                                <div className="p-3 bg-white border border-[#e9ecef] rounded-lg">
                                  <p className="text-xs text-[#7f8c8d] mb-1">License</p>
                                  <p className="text-sm font-mono font-semibold text-[#2c3e50]">{user.license}</p>
                                </div>
                              )}
                              {user.vehicleRegistration && (
                                <div className="p-3 bg-white border border-[#e9ecef] rounded-lg">
                                  <p className="text-xs text-[#7f8c8d] mb-1">Vehicle Reg.</p>
                                  <p className="text-sm font-mono font-semibold text-[#2c3e50]">{user.vehicleRegistration}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Broker ID */}
                        {user.brokerId && (
                          <div>
                            <p className="text-xs text-[#7f8c8d] mb-1">Broker ID</p>
                            <p className="text-sm font-mono font-semibold text-[#2c3e50]">{user.brokerId}</p>
                          </div>
                        )}

                        {/* Fleet */}
                        {user.Vehicles?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-[#7f8c8d] uppercase tracking-wide mb-2">Fleet ({user.Vehicles.length})</p>
                            <div className="grid grid-cols-2 gap-3">
                              {user.Vehicles.map((vehicle, vIdx) => (
                                <div key={vIdx} className="p-3 bg-white border border-[#e9ecef] rounded-lg">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="text-xs text-[#7f8c8d]">Type</p>
                                      <p className="text-sm font-semibold text-[#2c3e50]">{vehicle.vehicleType}</p>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${vehicle.status === 'active' ? 'bg-[#eafaf1] text-[#27ae60]' : 'bg-[#fef5e7] text-[#e67e22]'}`}>
                                      {vehicle.status || 'active'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-[#7f8c8d]">Reg. #</p>
                                  <p className="text-sm font-mono font-semibold text-[#2c3e50]">{vehicle.registrationNumber}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Ratings */}
                        {user.role === 'trucker' && (
                          <div>
                            <button
                              onClick={() => fetchUserReviews(user.id)}
                              className="text-sm font-medium text-[#3498db] hover:text-[#2980b9] mb-2"
                            >
                              {loadingReviews[user.id] ? 'Loading ratings…' : 'Load Ratings'}
                            </button>
                            {userReviews[user.id] && (
                              <div className="bg-white border border-[#e9ecef] rounded-lg p-4">
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                  <div>
                                    <p className="text-xs text-[#7f8c8d] mb-1">Avg Rating</p>
                                    <p className="text-lg font-bold text-[#2c3e50]">
                                      {userReviews[user.id].averageRating != null ? userReviews[user.id].averageRating.toFixed(1) : 'N/A'}
                                      <span className="text-[#f39c12] ml-1">★</span>
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-[#7f8c8d] mb-1">Total Reviews</p>
                                    <p className="text-lg font-bold text-[#2c3e50]">{userReviews[user.id].totalReviews}</p>
                                  </div>
                                </div>
                                {userReviews[user.id].distribution && (
                                  <div className="space-y-1.5">
                                    {[5, 4, 3, 2, 1].map(r => (
                                      <div key={r} className="flex items-center gap-2 text-xs">
                                        <span className="w-5 text-[#7f8c8d]">{r}★</span>
                                        <div className="flex-1 bg-[#ecf0f1] rounded-full h-1.5">
                                          <div
                                            className="bg-[#f39c12] h-1.5 rounded-full"
                                            style={{ width: `${Math.max(0, (userReviews[user.id].distribution[r] / Math.max(1, userReviews[user.id].totalReviews)) * 100)}%` }}
                                          />
                                        </div>
                                        <span className="w-5 text-right text-[#7f8c8d]">{userReviews[user.id].distribution[r] || 0}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white w-[520px] py-8 px-10 relative rounded-xl shadow-xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute left-4 top-4 text-[#7f8c8d] hover:text-[#2c3e50] text-lg"
            >
              ←
            </button>
            <h2 className="text-[1.4rem] font-semibold text-[#2c3e50] text-center mb-6">Add User</h2>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#e9ecef] w-[160px] h-[100px] mx-auto mb-6 rounded-lg cursor-pointer text-center hover:border-[#3498db] transition-colors">
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <>
                  <FiUpload size={20} className="text-[#bdc3c7] mb-1" />
                  <span className="text-xs text-[#7f8c8d]">Upload Image</span>
                </>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>

            <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Role</p>
            <div className="flex gap-3 mb-5">
              {['Admin', 'Agent'].map(r => (
                <label key={r} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#3498db]"
                    checked={formData.role === r}
                    onChange={() => handleRoleChange(r)}
                  />
                  <span className="text-sm text-[#2c3e50]">{r}</span>
                </label>
              ))}
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <label className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1 block">Name</label>
                <input name="name" value={formData.name} onChange={handleFormChange} placeholder="Full name"
                  className="w-full border border-[#e9ecef] rounded-lg px-3 py-2.5 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1 block">Email</label>
                <input name="email" value={formData.email} onChange={handleFormChange} placeholder="Email address"
                  className="w-full border border-[#e9ecef] rounded-lg px-3 py-2.5 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <label className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1 block">Password</label>
                  <input name="password" value={formData.password} onChange={handleFormChange}
                    type={showPassword ? 'text' : 'password'} placeholder="Password"
                    className="w-full border border-[#e9ecef] rounded-lg px-3 py-2.5 pr-9 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-[34px] text-[#7f8c8d]">
                    {showPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
                <div className="relative">
                  <label className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1 block">Confirm</label>
                  <input name="confirmPassword" value={formData.confirmPassword} onChange={handleFormChange}
                    type={showPassword ? 'text' : 'password'} placeholder="Confirm"
                    className="w-full border border-[#e9ecef] rounded-lg px-3 py-2.5 pr-9 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-[34px] text-[#7f8c8d]">
                    {showPassword ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handleAddUser}
              className="w-full bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Add User
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersPage
